import React, { useState, useEffect, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { checkIsDev, checkIsOwner, getPageAccess } from '@/lib/AuthSession';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'react-hot-toast';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';

import { Search, Clock, Calendar, ShieldCheck, UserCheck, Activity, Fingerprint, Laptop, History, Trophy, Users, AlertTriangle, Bot, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, MapPin, ScanFace, Camera, CheckCircle2 } from 'lucide-react';
import LateAbsencePopup from '@/components/attendance/LateAbsencePopup';
import ManualAttendanceModal from '@/components/attendance/ManualAttendanceModal';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import { getIndonesianHoliday, isHolidayOrSunday } from '@/lib/indonesiaHolidays';

export default function WorkspaceAbsensi() {
  const { member: currentMember } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Guard access based on page_access config
  useEffect(() => {
    if (!currentMember) return;
    const access = getPageAccess(currentMember, 'manajemen_tim');
    const isDev = currentMember?.role?.toLowerCase() === 'developer' || 
                  String(currentMember?.position || '').toLowerCase().includes('developer') || 
                  String(currentMember?.department || '').toLowerCase().includes('developer');
    const isAdminOrPrivileged = isDev || checkIsOwner(currentMember) || currentMember.role === 'admin';
    if (access === 'none' && !isAdminOrPrivileged) {
      navigate('/office');
    }
  }, [currentMember, navigate]);

  const [activeTab, setActiveTab] = useState('riwayat');
  const [search, setSearch] = useState(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      return p.get('search') || '';
    }
    return '';
  });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const q = p.get('search');
    if (q) setSearch(q);
  }, [location.search]);
  const [timeFilter, setTimeFilter] = useState('hari_ini');
  const [customDateStart, setCustomDateStart] = useState(new Date().toISOString().split('T')[0]);
  const [customDateEnd, setCustomDateEnd] = useState(new Date().toISOString().split('T')[0]);
  const [showAsaPopup, setShowAsaPopup] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState(null);

  const [editingLog, setEditingLog] = useState(null);
  const [editForm, setEditForm] = useState({ timestamp: '', notes: '' });
  
  const [editingStatsFor, setEditingStatsFor] = useState(null);
  const [statsForm, setStatsForm] = useState({ telat: '', hadir: '', sakit: '', izin: '', setengah_hari: '' });

  const [calendarMember, setCalendarMember] = useState(null);
  const [calendarMonthKey, setCalendarMonthKey] = useState('');
  const [dateStatusModal, setDateStatusModal] = useState(null);

  const applyDateStatus = async (newStatus) => {
    if (!dateStatusModal) return;
    const { memberId, dateStr, dayLogs } = dateStatusModal;
    const member = teamMembers.find(m => m.id === memberId);
    if (!member) return;

    try {
      if (dayLogs && dayLogs.length > 0) {
        await Promise.all(dayLogs.map(l => base44.entities.AttendanceLog.delete(l.id)));
      }

      if (newStatus !== 'alpa') {
        if (newStatus === 'hadir') {
          await base44.entities.AttendanceLog.create({
            employee_id: memberId,
            full_name: member.full_name,
            timestamp: `${dateStr}T08:00:00.000Z`,
            type: 'check_in',
            method: 'manual',
            notes: 'Input Kalender Presensi'
          });
          await base44.entities.AttendanceLog.create({
            employee_id: memberId,
            full_name: member.full_name,
            timestamp: `${dateStr}T17:00:00.000Z`,
            type: 'check_out',
            method: 'manual',
            notes: 'Input Kalender Presensi'
          });
        } else if (newStatus === 'telat') {
          await base44.entities.AttendanceLog.create({
            employee_id: memberId,
            full_name: member.full_name,
            timestamp: `${dateStr}T09:00:00.000Z`,
            type: 'check_in',
            method: 'manual',
            notes: 'Input Kalender Presensi | Telat (09:00)'
          });
          await base44.entities.AttendanceLog.create({
            employee_id: memberId,
            full_name: member.full_name,
            timestamp: `${dateStr}T17:00:00.000Z`,
            type: 'check_out',
            method: 'manual',
            notes: 'Input Kalender Presensi'
          });
        } else if (newStatus === 'setengah_hari') {
          await base44.entities.AttendanceLog.create({
            employee_id: memberId,
            full_name: member.full_name,
            timestamp: `${dateStr}T08:00:00.000Z`,
            type: 'check_in',
            method: 'manual',
            notes: 'Input Kalender Presensi | Setengah Hari'
          });
          await base44.entities.AttendanceLog.create({
            employee_id: memberId,
            full_name: member.full_name,
            timestamp: `${dateStr}T13:00:00.000Z`,
            type: 'check_out',
            method: 'manual',
            notes: 'Input Kalender Presensi | Setengah Hari'
          });
        } else if (newStatus === 'sakit') {
          await base44.entities.AttendanceLog.create({
            employee_id: memberId,
            full_name: member.full_name,
            timestamp: `${dateStr}T08:00:00.000Z`,
            type: 'sakit',
            method: 'manual',
            notes: 'Sakit (Input Kalender)'
          });
        } else if (newStatus === 'izin') {
          await base44.entities.AttendanceLog.create({
            employee_id: memberId,
            full_name: member.full_name,
            timestamp: `${dateStr}T08:00:00.000Z`,
            type: 'izin',
            method: 'manual',
            notes: 'Izin (Input Kalender)'
          });
        }
      }

      // Delete manual total override for this month on the TeamMember document so system logs recalculate dynamically!
      const monthKeyStr = dateStr.slice(0, 7);
      const existingByMonth = { ...(member?.absensi_override_by_month || {}) };
      delete existingByMonth[monthKeyStr];

      try {
        await base44.entities.TeamMember.update(memberId, {
          absensi_override: null,
          absensi_override_by_month: existingByMonth
        });
      } catch (e) {
        console.error("Failed to clear override on member:", e);
      }

      toast.success(`Presensi tanggal ${dateStr} berhasil disesuaikan!`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['attendance-logs'] }),
        queryClient.invalidateQueries({ queryKey: ['team-members'] }),
        queryClient.refetchQueries({ queryKey: ['attendance-logs'] }),
        queryClient.refetchQueries({ queryKey: ['team-members'] })
      ]);
      setDateStatusModal(null);
    } catch (e) {
      toast.error('Gagal memperbarui presensi tanggal.');
    }
  };

  const isNewerThan24h = (timestamp) => {
    if (!timestamp) return false;
    const diffMs = new Date() - new Date(timestamp);
    return diffMs < 24 * 60 * 60 * 1000;
  };

  const startEditLog = (log, emp = null) => {
    if (!log) return;
    setEditingLog({
      ...log,
      employeeName: emp?.full_name || log.full_name || 'Karyawan',
      dateStr: emp?.date || (log.timestamp ? log.timestamp.split('T')[0] : new Date().toISOString().split('T')[0])
    });
    
    let formattedTime = '';
    if (log.timestamp) {
      const d = new Date(log.timestamp);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      formattedTime = `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    }
    setEditForm({
      timestamp: formattedTime,
      notes: log.notes || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingLog) return;
    try {
      let finalNotes = editForm.notes;
      const newTime = new Date(editForm.timestamp);
      
      // Auto-update status telat jika jam masuk dikoreksi sebelum 08:00
      if (editingLog.type === 'check_in') {
        const hours = newTime.getHours();
        const minutes = newTime.getMinutes();
        const isLate = hours > 8 || (hours === 8 && minutes > 0);
        if (!isLate) {
          finalNotes = finalNotes.replace(/\s*\|\s*Telat:.*?(?=(\s*\||$))/i, '').trim();
          finalNotes = finalNotes.replace(/\bTelat\b/ig, '').trim();
        }
      }

      await base44.entities.AttendanceLog.update(editingLog.id, {
        timestamp: new Date(editForm.timestamp).toISOString(),
        notes: finalNotes
      });
      toast.success("Jam presensi berhasil diperbarui!");
      setEditingLog(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["attendance-logs-all"] }),
        queryClient.refetchQueries({ queryKey: ["attendance-logs-all"] })
      ]);
    } catch (e) {
      toast.error("Gagal memperbarui jam presensi.");
      console.error(e);
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm("Yakin ingin menghapus log absensi ini?")) return;
    try {
      await base44.entities.AttendanceLog.delete(logId);
      toast.success("Log absensi berhasil dihapus!");
      queryClient.invalidateQueries({ queryKey: ["attendance-logs-all"] });
    } catch (e) {
      toast.error("Gagal menghapus log absensi.");
      console.error(e);
    }
  };

  const handleBatchDelete = async () => {
    if (!confirm(`Hapus semua log absensi terpilih?`)) return;
    try {
      const { start, end } = getDateBounds();
      const toDeleteLogs = allLogs.filter(log => {
        if (!log.timestamp || !selectedIds.has(log.employee_id)) return false;
        if (!start || !end) return true;
        const logDate = new Date(log.timestamp);
        return logDate >= start && logDate <= end;
      });
      
      await Promise.all(toDeleteLogs.map(log => base44.entities.AttendanceLog.delete(log.id)));
      
      toast.success("Berhasil menghapus log absensi terpilih!");
      clearSelection();
      queryClient.invalidateQueries({ queryKey: ["attendance-logs-all"] });
    } catch (err) {
      console.error(err);
      toast.error("Gagal menghapus log absensi.");
    }
  };

  // Fetch Team Members
  const { data: teamMembers = [], isLoading: isLoadingTeam } = useQuery({
    queryKey: ["team-members"],
    queryFn: () => base44.entities.TeamMember.list(),
  });

  // Fetch ALL Attendance Logs (for overall stats)
  const { data: allLogs = [] } = useQuery({
    queryKey: ["attendance-logs-all"],
    queryFn: async () => {
      return await base44.entities.AttendanceLog.list("-timestamp");
    },
  });

  useEffect(() => {
    const cleanupOldPhotos = async () => {
      const oldLogs = allLogs.filter(log => {
        if (!log.photo_url) return false;
        const diffMs = new Date() - new Date(log.timestamp);
        return diffMs > 24 * 60 * 60 * 1000; // Older than 24h
      });

      for (const log of oldLogs) {
        try {
          await base44.entities.AttendanceLog.update(log.id, { photo_url: null });
        } catch (e) {
          console.error("Failed to clear old photo_url in DB:", e);
        }
      }
    };

    const autoCheckoutOldLogs = async () => {
      const oldCheckIns = allLogs.filter(log => {
        if (log.type !== 'check_in') return false;
        const diffMs = new Date() - new Date(log.timestamp);
        return diffMs > 24 * 60 * 60 * 1000;
      });

      const newCheckoutPromises = [];
      let needsRefetch = false;

      for (const ciLog of oldCheckIns) {
        const dateStr = ciLog.timestamp.split('T')[0];
        const hasCheckout = allLogs.some(log => 
          log.employee_id === ciLog.employee_id && 
          log.type === 'check_out' && 
          log.timestamp.startsWith(dateStr)
        );

        if (!hasCheckout) {
          const autoLog = {
            employee_id: ciLog.employee_id,
            full_name: ciLog.full_name,
            timestamp: `${dateStr}T17:00:00`,
            type: 'check_out',
            method: 'auto_system',
            notes: 'Sistem: Otomatis checkout jam 17:00'
          };
          newCheckoutPromises.push(base44.entities.AttendanceLog.create(autoLog));
          needsRefetch = true;
        }
      }
      
      if (newCheckoutPromises.length > 0) {
        try {
           await Promise.all(newCheckoutPromises);
           if (needsRefetch) {
             queryClient.invalidateQueries({ queryKey: ["attendance-logs-all"] });
           }
        } catch(e) {
           console.error("Auto checkout failed", e);
        }
      }
    };

    if (allLogs.length > 0) {
      cleanupOldPhotos();
      autoCheckoutOldLogs();
    }
  }, [allLogs, queryClient]);

  // Helper to get date bounds
  const getDateBounds = () => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23,59,59,999);
    
    if (timeFilter === 'hari_ini') {
      return { start: today, end: endOfDay };
    }
    if (timeFilter === 'kemarin') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayEnd = new Date(yesterday);
      yesterdayEnd.setHours(23,59,59,999);
      return { start: yesterday, end: yesterdayEnd };
    }
    if (timeFilter === '7_hari') {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { start, end: endOfDay };
    }
    if (timeFilter === 'bulan_ini' || timeFilter === 'all_time') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start, end: endOfDay };
    }
    if (timeFilter === 'bulan_kemarin') {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      end.setHours(23,59,59,999);
      return { start, end };
    }
    if (timeFilter === 'custom') {
      const start = new Date(customDateStart);
      start.setHours(0,0,0,0);
      const end = new Date(customDateEnd);
      end.setHours(23,59,59,999);
      return { start, end };
    }
    return { start: null, end: null };
  };

  const filteredAllLogs = useMemo(() => {
    const { start, end } = getDateBounds();
    if (!start || !end) return allLogs;
    
    return allLogs.filter(log => {
      if (!log.timestamp) return false;
      const logDate = new Date(log.timestamp);
      return logDate >= start && logDate <= end;
    });
  }, [allLogs, timeFilter, customDateStart, customDateEnd]);

  // Filter logs for "Riwayat" tab (filtered by search and date)
  const displayLogs = [...filteredAllLogs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const filteredLogs = displayLogs.filter(log => {
    const q = search.toLowerCase();
    return !q || (log.full_name || '').toLowerCase().includes(q) || (log.employee_id || '').toLowerCase().includes(q);
  });

  // Helper to retrieve standardized YYYY-MM-DD join date for an employee
  const getMemberJoinDateStr = (member) => {
    if (!member) return null;
    if (member.join_date) return String(member.join_date).slice(0, 10);
    if (member.created_date) return String(member.created_date).slice(0, 10);
    return null;
  };

  // Helper to compute working days in a date range (Deducting Sundays, Saturday off, and Tanggal Merah)
  const getWorkingDaysInRange = (startDate, endDate, isSatOff) => {
    let count = 0;
    const cur = new Date(startDate);
    const end = new Date(endDate);
    cur.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    while (cur <= end) {
      const day = cur.getDay(); // 0=Sun, 6=Sat
      const isSunday = day === 0;
      const isSaturday = day === 6;
      const isHoliday = !!getIndonesianHoliday(cur);
      if (!isSunday && !(isSaturday && isSatOff) && !isHoliday) {
        count++;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };

  // Helper to compute stats for a single specific month (e.g. year: 2026, monthIdx: 6 for July)
  const getSingleMonthStats = (empId, targetYear, targetMonthIdx) => {
    const member = teamMembers.find(m => m.id === empId);
    const mKey = `${targetYear}-${String(targetMonthIdx + 1).padStart(2, '0')}`;
    
    // 1. Calculate actual system logs for that month
    const monthLogs = allLogs.filter(l => {
      if (l.employee_id !== empId || !l.timestamp) return false;
      const d = new Date(l.timestamp);
      return d.getFullYear() === targetYear && d.getMonth() === targetMonthIdx;
    });

    const checkIns = monthLogs.filter(l => l.type === 'check_in');
    const sysHadir = new Set(checkIns.map(l => l.timestamp.slice(0, 10))).size;
    const sysTelat = checkIns.filter(l => l.notes && l.notes.toLowerCase().includes('telat')).length;
    const sysSakit = monthLogs.filter(l => l.type === 'sakit' || (l.notes && l.notes.toLowerCase().includes('sakit'))).length;
    const sysIzin = monthLogs.filter(l => l.type === 'izin' || (l.notes && l.notes.toLowerCase().includes('izin'))).length;
    const sysSetengahHari = monthLogs.filter(l => l.notes && l.notes.toLowerCase().includes('setengah hari')).length;

    const now = new Date();
    const isCurrentMonth = now.getFullYear() === targetYear && now.getMonth() === targetMonthIdx;
    let expected = 0;
    const cur = new Date(targetYear, targetMonthIdx, 1);
    const endOfMonth = new Date(targetYear, targetMonthIdx + 1, 0);
    const limitDate = isCurrentMonth ? now : endOfMonth;

    // Batasi tanggal awal perhitungan sesuai tanggal bergabung karyawan
    const joinDateStr = getMemberJoinDateStr(member);
    if (joinDateStr) {
      const [jy, jm, jd] = joinDateStr.split('-').map(Number);
      if (!isNaN(jy) && !isNaN(jm) && !isNaN(jd)) {
        const joinDate = new Date(jy, jm - 1, jd);
        joinDate.setHours(0, 0, 0, 0);
        if (joinDate > cur) {
          cur.setTime(joinDate.getTime());
        }
      }
    }

    while (cur <= limitDate) {
      const day = cur.getDay();
      const isSunday = day === 0;
      const isSaturday = day === 6;
      const isSatOff = member?.saturday_off === true;
      const isHoliday = !!getIndonesianHoliday(cur);
      if (!isSunday && !(isSaturday && isSatOff) && !isHoliday) expected++;
      cur.setDate(cur.getDate() + 1);
    }
    const sysAlpa = Math.max(0, expected - (sysHadir + sysSakit + sysIzin + sysSetengahHari));

    const sysOriginal = { hadir: sysHadir, telat: sysTelat, sakit: sysSakit, izin: sysIzin, setengah_hari: sysSetengahHari, alpa: sysAlpa };

    // 2. Check if user set a direct override for this month
    const mOv = member?.absensi_override_by_month?.[mKey];
    if (mOv && typeof mOv === 'object') {
      return {
        hadir: mOv.hadir !== undefined ? Math.max(0, Number(mOv.hadir)) : sysHadir,
        telat: mOv.telat !== undefined ? Math.max(0, Number(mOv.telat)) : sysTelat,
        sakit: mOv.sakit !== undefined ? Math.max(0, Number(mOv.sakit)) : sysSakit,
        izin: mOv.izin !== undefined ? Math.max(0, Number(mOv.izin)) : sysIzin,
        setengah_hari: mOv.setengah_hari !== undefined ? Math.max(0, Number(mOv.setengah_hari)) : sysSetengahHari,
        alpa: mOv.alpa !== undefined ? Math.max(0, Number(mOv.alpa)) : sysAlpa,
        expected,
        original: sysOriginal
      };
    }

    return { hadir: sysHadir, telat: sysTelat, sakit: sysSakit, izin: sysIzin, setengah_hari: sysSetengahHari, alpa: sysAlpa, expected, original: sysOriginal };
  };

  // Calculate Overall Stats per Employee for "Detail Karyawan" Card
  const getEmployeeStats = (empId) => {
    const member = teamMembers.find(m => m.id === empId);
    const today = new Date();
    const curYear = today.getFullYear();
    const curMonthIdx = today.getMonth();

    // Bulan Ini (Current Month, e.g. Aug 2026)
    const currentMonthStats = getSingleMonthStats(empId, curYear, curMonthIdx);

    // Bulan Kemarin (Previous Month, e.g. July 2026)
    const prevDate = new Date(curYear, curMonthIdx - 1, 1);
    const prevYear = prevDate.getFullYear();
    const prevMonthIdx = prevDate.getMonth();
    const prevMonthStats = getSingleMonthStats(empId, prevYear, prevMonthIdx);

    if (timeFilter === 'bulan_kemarin') {
      return currentMonthStats ? prevMonthStats : prevMonthStats;
    }

    if (timeFilter === 'bulan_ini') {
      return currentMonthStats;
    }

    if (timeFilter === 'all_time') {
      // Accumulate across all historical months that exist in logs or overrides
      const byMonthMap = member?.absensi_override_by_month || {};
      const monthKeysSet = new Set(Object.keys(byMonthMap));

      allLogs.filter(l => l.employee_id === empId).forEach(l => {
        if (l.timestamp) monthKeysSet.add(l.timestamp.slice(0, 7));
      });
      // Ensure current & prev month are included
      monthKeysSet.add(`${curYear}-${String(curMonthIdx + 1).padStart(2, '0')}`);
      monthKeysSet.add(`${prevYear}-${String(prevMonthIdx + 1).padStart(2, '0')}`);

      let totHadir = 0, totTelat = 0, totSakit = 0, totIzin = 0, totSetengahHari = 0, totAlpa = 0, totExpected = 0;
      let origHadir = 0, origTelat = 0, origSakit = 0, origIzin = 0, origSetengahHari = 0, origAlpa = 0;

      monthKeysSet.forEach(mKey => {
        const [yStr, mStr] = mKey.split('-');
        const y = parseInt(yStr);
        const mIdx = parseInt(mStr) - 1;
        if (!isNaN(y) && !isNaN(mIdx)) {
          const mStats = getSingleMonthStats(empId, y, mIdx);
          totHadir += mStats.hadir;
          totTelat += mStats.telat;
          totSakit += mStats.sakit;
          totIzin += mStats.izin;
          totSetengahHari += mStats.setengah_hari;
          totAlpa += mStats.alpa;
          totExpected += mStats.expected;

          origHadir += mStats.original.hadir;
          origTelat += mStats.original.telat;
          origSakit += mStats.original.sakit;
          origIzin += mStats.original.izin;
          origSetengahHari += mStats.original.setengah_hari;
          origAlpa += mStats.original.alpa;
        }
      });

      return {
        hadir: totHadir,
        telat: totTelat,
        sakit: totSakit,
        izin: totIzin,
        setengah_hari: totSetengahHari,
        alpa: totAlpa,
        expected: member?.saturday_off === true ? 20 : 22,
        original: { hadir: origHadir, telat: origTelat, sakit: origSakit, izin: origIzin, setengah_hari: origSetengahHari, alpa: origAlpa }
      };
    }

    if (['hari_ini', 'kemarin', '7_hari', 'custom'].includes(timeFilter)) {
      const empLogs = filteredAllLogs.filter(l => l.employee_id === empId);
      const checkIns = empLogs.filter(l => l.type === 'check_in');
      const h = new Set(checkIns.map(l => l.timestamp.slice(0,10))).size;
      const t = checkIns.filter(l => l.notes && l.notes.toLowerCase().includes('telat')).length;
      const s = empLogs.filter(l => l.type === 'sakit' || (l.notes && l.notes.toLowerCase().includes('sakit'))).length;
      const i = empLogs.filter(l => l.type === 'izin' || (l.notes && l.notes.toLowerCase().includes('izin'))).length;
      const sh = empLogs.filter(l => l.notes && l.notes.toLowerCase().includes('setengah hari')).length;

      const now = new Date();
      let rangeStart = now;
      let rangeEnd = now;

      if (timeFilter === 'kemarin') {
        const yest = new Date();
        yest.setDate(now.getDate() - 1);
        rangeStart = yest;
        rangeEnd = yest;
      } else if (timeFilter === '7_hari') {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(now.getDate() - 6);
        rangeStart = sevenDaysAgo;
        rangeEnd = now;
      } else if (timeFilter === 'custom') {
        rangeStart = new Date(customDateStart);
        rangeEnd = new Date(customDateEnd);
      }

      // Sesuaikan batas awal agar tidak menghitung hari sebelum karyawan bergabung
      const joinDateStr = getMemberJoinDateStr(member);
      if (joinDateStr) {
        const [jy, jm, jd] = joinDateStr.split('-').map(Number);
        if (!isNaN(jy) && !isNaN(jm) && !isNaN(jd)) {
          const joinDate = new Date(jy, jm - 1, jd);
          joinDate.setHours(0, 0, 0, 0);
          if (joinDate > rangeStart) {
            rangeStart = new Date(joinDate);
          }
        }
      }

      rangeStart.setHours(0, 0, 0, 0);
      rangeEnd.setHours(23, 59, 59, 999);

      let expected = 0;
      if (rangeStart <= rangeEnd) {
        expected = getWorkingDaysInRange(rangeStart, rangeEnd, member?.saturday_off === true);
      }
      const alpa = Math.max(0, expected - (h + s + i + sh));

      return {
        hadir: h, telat: t, sakit: s, izin: i, setengah_hari: sh, alpa, expected,
        original: { hadir: h, telat: t, sakit: s, izin: i, setengah_hari: sh, alpa }
      };
    }

    return currentMonthStats;
  };

  const displayMembers = teamMembers.filter(m => m.require_attendance !== false);

  const groupedEmpList = useMemo(() => {
    return Object.values(filteredLogs.reduce((acc, log) => {
      const dateStr = log.timestamp.split('T')[0];
      const key = `${log.employee_id}_${dateStr}`;
      if (!acc[key]) acc[key] = { id: key, employee_id: log.employee_id, date: dateStr, full_name: log.full_name, photo_url: log.photo_url || log.face_image, check_in: null, check_out: null, methods: [], notes: [] };
      const emp = acc[key];
      if (log.photo_url || log.face_image) emp.photo_url = log.photo_url || log.face_image;
      if (log.type === 'check_in' && (!emp.check_in || log.timestamp < emp.check_in.timestamp)) {
        emp.check_in = log;
        if (log.photo_url || log.face_image) emp.photo_url = log.photo_url || log.face_image;
      }
      if (log.type === 'check_out' && (!emp.check_out || log.timestamp > emp.check_out.timestamp)) emp.check_out = log;
      if (log.method && !emp.methods.includes(log.method)) emp.methods.push(log.method);
      if (log.notes && !emp.notes.includes(log.notes)) emp.notes.push(log.notes);
      return acc;
    }, {}));
  }, [filteredLogs]);

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    selectedCount,
    totalCount,
    startSelection
  } = useBatchSelection(groupedEmpList, emp => emp.employee_id);

  return (
    <div className="space-y-6 text-[#302621]">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-100 pb-5">
        <div>
          <h1 className="text-2xl font-black text-[#302621] tracking-tight">Analytics Kehadiran</h1>
          <p className="text-xs text-[#7A695B] mt-0.5">Pemantauan log, statistik, dan rekap performa kedisiplinan staf.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <Select value={timeFilter} onValueChange={setTimeFilter}>
            <SelectTrigger className="h-9 w-[160px] text-xs bg-white rounded-xl border-slate-200 focus:ring-[#8C6208]">
              <SelectValue placeholder="Pilih Waktu" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="hari_ini" className="text-xs">Hari Ini</SelectItem>
              <SelectItem value="kemarin" className="text-xs">Kemarin</SelectItem>
              <SelectItem value="7_hari" className="text-xs">7 Hari Terakhir</SelectItem>
              <SelectItem value="bulan_ini" className="text-xs">Bulan Ini</SelectItem>
              <SelectItem value="bulan_kemarin" className="text-xs">Bulan Kemarin</SelectItem>
              <SelectItem value="all_time" className="text-xs">Semua Waktu</SelectItem>
              <SelectItem value="custom" className="text-xs">Custom Tanggal</SelectItem>
            </SelectContent>
          </Select>
          {timeFilter === 'custom' && (
            <div className="flex items-center gap-2 bg-white p-1 border border-slate-200 rounded-xl">
              <CustomDatePicker className="w-28 h-7 text-[10px] sm:text-xs border-none rounded-lg focus-visible:ring-0 px-2" value={customDateStart} onChange={setCustomDateStart} />
              <span className="text-slate-400 text-[10px] font-bold">-</span>
              <CustomDatePicker className="w-28 h-7 text-[10px] sm:text-xs border-none rounded-lg focus-visible:ring-0 px-2" value={customDateEnd} onChange={setCustomDateEnd} />
            </div>
          )}
          {currentMember && (checkIsDev(currentMember) || checkIsOwner(currentMember) || currentMember.role === 'admin') && (
            <>
              <Button 
                onClick={() => isSelectionMode ? clearSelection() : startSelection()}
                variant="outline"
                className={`h-9 gap-1.5 text-xs font-bold rounded-xl ${isSelectionMode ? 'border-red-200 text-red-700 hover:bg-red-50 bg-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50 bg-white'}`}
              >
                {isSelectionMode ? 'Batal Pilih' : 'Pilih Banyak'}
              </Button>
              <Button 
                onClick={() => setShowManualModal(true)}
                variant="outline"
                className="h-9 gap-1.5 text-xs font-bold rounded-xl border-[#D4AF37] text-[#8C6208] hover:bg-[#F9F5EC]"
              >
                <Plus className="w-4 h-4" /> Input Absen Manual
              </Button>
            </>
          )}

          {currentMember?.role === 'developer' && (
            <Button 
              onClick={() => setShowAsaPopup(true)}
              className="h-9 gap-1.5 text-xs bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl"
            >
              <Bot className="w-4 h-4" /> Simulasi Pop-Up ASA
            </Button>
          )}
        </div>
      </div>

      <LateAbsencePopup 
        open={showAsaPopup} 
        onClose={() => setShowAsaPopup(false)} 
        memberName={currentMember?.full_name} 
      />

      <ManualAttendanceModal 
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        teamMembers={displayMembers}
      />

      {/* TABS NAVIGATION */}
      <div className="flex flex-wrap gap-2 border-b border-[#E6D5B8]/30 pb-3">
        <Button 
          variant={activeTab === 'riwayat' ? 'default' : 'outline'} 
          className={`h-9 text-xs font-bold rounded-xl gap-2 transition-all ${activeTab === 'riwayat' ? 'bg-[#302621] text-white' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
          onClick={() => setActiveTab('riwayat')}
        >
          <History className="w-4 h-4" /> Riwayat Absen
        </Button>
        <Button 
          variant={activeTab === 'ranking' ? 'default' : 'outline'} 
          className={`h-9 text-xs font-bold rounded-xl gap-2 transition-all ${activeTab === 'ranking' ? 'bg-[#302621] text-white' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
          onClick={() => setActiveTab('ranking')}
        >
          <Trophy className="w-4 h-4" /> Ranking & Rekap
        </Button>
        <Button 
          variant={activeTab === 'detail' ? 'default' : 'outline'} 
          className={`h-9 text-xs font-bold rounded-xl gap-2 transition-all ${activeTab === 'detail' ? 'bg-[#302621] text-white' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
          onClick={() => setActiveTab('detail')}
        >
          <Users className="w-4 h-4" /> Detail Karyawan
        </Button>
      </div>

      {/* TAB 1: RIWAYAT ABSEN */}
      {activeTab === 'riwayat' && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          {isSelectionMode && (
            <BatchActionBar 
              selectedCount={selectedCount}
              totalCount={totalCount}
              onSelectAll={selectAll}
              onClear={clearSelection}
              actions={[
                {
                  label: 'Hapus Terpilih',
                  icon: <Trash2 className="w-4 h-4" />,
                  onClick: handleBatchDelete,
                  variant: 'destructive'
                }
              ]}
            />
          )}
          {/* Stats Board */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-[#E6D5B8]/40 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <UserCheck className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Total Check-In</p>
                  <p className="text-xl font-black text-[#302621]">{filteredLogs.filter(l => l.type === 'check_in').length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-[#E6D5B8]/40 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Terlambat</p>
                  <p className="text-xl font-black text-[#302621]">0</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-[#E6D5B8]/40 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                  <Activity className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Sakit / Izin</p>
                  <p className="text-xl font-black text-[#302621]">0</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-[#E6D5B8]/40 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Total Staf</p>
                  <p className="text-xl font-black text-[#302621]">{displayMembers.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A39171]" />
              <Input placeholder="Cari nama staf..." className="pl-8 h-9 text-xs w-full sm:w-64 bg-white focus-visible:ring-[#8C6208] rounded-xl" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          {isSelectionMode && (
            <div className="mt-4">
              <BatchActionBar 
                selectedCount={selectedCount}
                totalCount={totalCount}
                onSelectAll={selectAll}
                onClear={clearSelection}
                actions={[
                  {
                    label: 'Hapus Terpilih',
                    icon: <Trash2 className="w-4 h-4" />,
                    onClick: handleBatchDelete,
                    variant: 'destructive'
                  }
                ]}
              />
            </div>
          )}

          {/* Logs Table */}
          <div className="bg-white border border-[#E6D5B8]/40 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                    {isSelectionMode && (
                      <th className="px-4 py-3 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={selectedCount > 0 && selectedCount === totalCount}
                          onChange={(e) => {
                            if (e.target.checked) selectAll();
                            else clearSelection();
                          }}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-[#8C6208] focus:ring-[#D4AF37] cursor-pointer"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 font-bold">Karyawan</th>
                    <th className="px-4 py-3 font-bold">Tanggal</th>
                    <th className="px-4 py-3 font-bold">Masuk & Keluar</th>
                    <th className="px-4 py-3 font-bold">Jam Kerja</th>
                    <th className="px-4 py-3 font-bold">Metode / Catatan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {groupedEmpList.map(emp => {
                    const checkInTime = emp.check_in ? new Date(emp.check_in.timestamp).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) : '-';
                    const checkOutTime = emp.check_out ? new Date(emp.check_out.timestamp).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) : '-';
                    let workDuration = '-';
                    if (emp.check_in && emp.check_out) {
                      const ms = new Date(emp.check_out.timestamp) - new Date(emp.check_in.timestamp);
                      const hrs = Math.floor(ms / 3600000);
                      const mins = Math.floor((ms % 3600000) / 60000);
                      workDuration = `${hrs}j ${mins}m`;
                    } else if (emp.check_in) {
                      workDuration = 'Sedang Bekerja';
                    }

                    const memberProfile = teamMembers.find(m => m.id === emp.employee_id);
                    const profilePhoto = memberProfile?.photo_url;

                    return (
                      <tr key={emp.id} className={`hover:bg-slate-50/50 transition-colors group ${selectedIds.has(emp.employee_id) ? 'bg-amber-50/20' : ''}`}>
                        {isSelectionMode && (
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(emp.employee_id)}
                              onChange={() => toggleSelection(emp.employee_id)}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-[#8C6208] focus:ring-[#D4AF37] cursor-pointer"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {profilePhoto ? (
                              <img src={profilePhoto} alt="Profile" className="w-8 h-8 rounded-full object-cover border shadow-sm shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                <UserCheck className="w-4 h-4 text-slate-400" />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="font-bold text-slate-800">{emp.full_name || 'Tidak Diketahui'}</p>
                                <button 
                                  type="button"
                                  onClick={() => setViewingPhoto({
                                    empPhoto: emp.photo_url,
                                    refPhoto: memberProfile?.face_photo_url || memberProfile?.photo_url,
                                    name: emp.full_name || memberProfile?.full_name,
                                    notes: emp.notes,
                                    hasDescriptor: Boolean(memberProfile?.face_descriptor)
                                  })}
                                  className="inline-flex items-center gap-1 text-[9.5px] px-2 py-0.5 rounded-full font-bold text-[#8C6208] bg-amber-50 hover:bg-amber-100 border border-amber-200/90 transition-all cursor-pointer shadow-2xs active:scale-95"
                                  title="Lihat & Bandingkan Foto Acuan Wajah Terdaftar"
                                >
                                  <ScanFace className="w-3 h-3 text-[#8C6208]" />
                                  <span>Acuan Wajah</span>
                                </button>
                              </div>
                              <p className="text-[10px] text-slate-400 font-mono mt-0.5">{emp.employee_id || '-'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-bold text-slate-800 text-xs">
                            {new Date(emp.date).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-2">
                              <p className="text-emerald-700 font-bold"><span className="text-slate-400 font-normal mr-1">In:</span> {checkInTime}</p>
                              {emp.check_in ? (
                                <div className="flex items-center gap-1">
                                  <button 
                                    onClick={() => startEditLog(emp.check_in, emp)} 
                                    className="p-1 rounded-md text-slate-400 hover:text-[#8C6208] hover:bg-amber-50 transition-colors cursor-pointer" 
                                    title="Edit Jam Masuk"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); handleDeleteLog(emp.check_in.id); }} 
                                    className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer" 
                                    title="Hapus Log Masuk"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setShowManualModal(true)}
                                  className="text-[10px] text-amber-700 hover:underline font-semibold"
                                  title="Input manual jam masuk"
                                >
                                  + Masuk
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-rose-700 font-bold"><span className="text-slate-400 font-normal mr-1">Out:</span> {checkOutTime}</p>
                              {emp.check_out ? (
                                <div className="flex items-center gap-1">
                                  <button 
                                    onClick={() => startEditLog(emp.check_out, emp)} 
                                    className="p-1 rounded-md text-slate-400 hover:text-[#8C6208] hover:bg-amber-50 transition-colors cursor-pointer" 
                                    title="Edit Jam Pulang"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); handleDeleteLog(emp.check_out.id); }} 
                                    className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer" 
                                    title="Hapus Log Pulang"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : emp.check_in ? (
                                <button
                                  onClick={() => setShowManualModal(true)}
                                  className="text-[10px] text-slate-400 hover:text-rose-600 hover:underline font-semibold"
                                  title="Input manual jam pulang"
                                >
                                  + Pulang
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-[10px] px-2 h-5 font-bold uppercase bg-blue-50 text-blue-700 border-blue-200`}>
                            {workDuration}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <div className="flex flex-wrap items-center gap-1.5 text-slate-500 max-w-[200px]">
                              {emp.methods.map((method, idx) => {
                                if (method === 'web_portal_face_mismatch' || emp.notes.some(n => n.includes('Wajah Beda') || n.includes('Wajah Tidak Cocok'))) {
                                  return (
                                    <Badge key={idx} variant="secondary" className="text-[9px] bg-rose-50 text-rose-700 border border-rose-300 font-bold flex items-center gap-1 shadow-sm">
                                      <span>⚠️ Wajah Beda</span>
                                    </Badge>
                                  );
                                }
                                if (method === 'web_portal_face_noface' || emp.notes.some(n => n.includes('Wajah Tidak Terdeteksi'))) {
                                  return (
                                    <Badge key={idx} variant="secondary" className="text-[9px] bg-amber-50 text-amber-800 border border-amber-300 font-bold flex items-center gap-1 shadow-sm">
                                      <span>⚠️ Tanpa Wajah</span>
                                    </Badge>
                                  );
                                }
                                if (method === 'web_portal_face_noref' || emp.notes.some(n => n.includes('Belum Ada Acuan'))) {
                                  return (
                                    <Badge key={idx} variant="secondary" className="text-[9px] bg-blue-50 text-blue-700 border border-blue-200 font-medium flex items-center gap-1 shadow-sm">
                                      <span>ℹ️ Belum Acuan</span>
                                    </Badge>
                                  );
                                }
                                if (method === 'web_portal_face') {
                                  return (
                                    <Badge key={idx} variant="secondary" className={`text-[9px] ${emp.photo_url ? 'bg-amber-50 text-[#8C6208] border border-amber-200 font-bold' : 'bg-slate-100 text-slate-600'}`}>
                                      {emp.photo_url ? '📷 Face ID' : 'Face ID'}
                                    </Badge>
                                  );
                                }
                                return (
                                  <Badge key={idx} variant="secondary" className={`text-[9px] ${method === 'manual_by_admin' || method === 'manual' ? 'bg-orange-50 text-orange-700 border border-orange-200 font-medium' : 'bg-slate-100 text-slate-600'}`}>
                                    {method === 'manual_by_admin' || method === 'manual' ? '📝 Input Manual' : method === 'web_portal' ? '🌐 Web Portal' : 'ADMS'}
                                  </Badge>
                                );
                              })}
                              {emp.photo_url && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-[9px] h-5 px-1.5 py-0 font-bold border-amber-200 text-[#8C6208] bg-[#F9F5EC] hover:bg-amber-100 rounded-lg flex items-center gap-1 cursor-pointer"
                                  onClick={() => setViewingPhoto({
                                    empPhoto: emp.photo_url,
                                    refPhoto: memberProfile?.face_photo_url || memberProfile?.photo_url,
                                    name: emp.full_name || memberProfile?.full_name,
                                    notes: emp.notes,
                                    hasDescriptor: Boolean(memberProfile?.face_descriptor)
                                  })}
                                >
                                  Lihat Foto
                                </Button>
                              )}
                            </div>
                            {emp.notes.length > 0 && (
                              <div className="flex flex-col gap-1 mt-1 max-w-[280px]">
                                {emp.notes.map((note, nIdx) => {
                                  if (note.startsWith('Area: ')) {
                                    const areaPart = note.split(' | ')[0]?.replace('Area: ', '');
                                    const otherParts = note.split(' | ').slice(1).join(' | ');
                                    return (
                                      <div key={nIdx} className="flex flex-col gap-0.5">
                                        <span className="inline-flex items-center gap-1 text-[9.5px] font-semibold text-amber-900 bg-amber-50 border border-amber-200/70 px-2 py-0.5 rounded-md w-fit">
                                          <MapPin className="w-2.5 h-2.5 text-[#8C6208]" />
                                          {areaPart}
                                        </span>
                                        {otherParts && (
                                          <span className={`text-[10px] truncate ${otherParts.includes('⚠️') ? 'text-rose-600 font-bold' : 'text-slate-500'}`} title={otherParts}>
                                            {otherParts}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  }
                                  return (
                                    <span 
                                      key={nIdx} 
                                      className={`text-[10px] truncate ${note.includes('⚠️') ? 'text-rose-600 font-bold' : 'text-slate-500'}`} 
                                      title={note}
                                    >
                                      {note}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {groupedEmpList.length === 0 && (
                    <tr>
                      <td colSpan={isSelectionMode ? 5 : 4} className="px-4 py-8 text-center text-slate-400 text-xs">
                        Tidak ada log absensi di tanggal ini.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: RANKING & REKAP */}
      {activeTab === 'ranking' && (
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="bg-white border border-[#E6D5B8]/40 rounded-2xl p-8 shadow-sm flex flex-col items-center justify-center text-center space-y-3">
            <div className="w-16 h-16 bg-[#F9F5EC] rounded-full flex items-center justify-center text-[#8C6208]">
              <Trophy className="w-8 h-8" />
            </div>
            <h3 className="font-black text-lg text-[#302621]">Papan Peringkat Sedang Dihitung</h3>
            <p className="text-xs text-slate-500 max-w-sm">Data sedang dikumpulkan dari mesin ADMS. Rekapitulasi penuh akan muncul setelah setidaknya ada 1 minggu log kehadiran berturut-turut.</p>
          </div>
        </div>
      )}

      {/* TAB 3: DETAIL KARYAWAN (OVERALL CARDS) */}
      {activeTab === 'detail' && (
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {displayMembers.map(member => {
              const stats = getEmployeeStats(member.id);
              // Simple avatar generation
              const initials = member.full_name?.substring(0, 2).toUpperCase() || 'ST';
              
              return (
                <div key={member.id} className="bg-white border border-[#E6D5B8]/40 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                  <div className="flex items-center gap-3 border-b border-slate-100 pb-4 mb-4 relative">
                    <div className="absolute top-0 right-0 flex gap-2 hidden sm:flex">
                      <div className="bg-[#F9F5EC] border border-[#D4AF37]/40 text-[#8C6208] text-[9px] font-bold px-2 py-1 rounded-lg uppercase tracking-wider shadow-sm">
                        Wajib Absen: {stats.expected} Hari
                      </div>
                      <div className="bg-slate-50 border border-slate-200 text-slate-500 text-[9px] font-bold px-2 py-1 rounded-lg uppercase tracking-wider shadow-sm">
                        Total Absen: {stats.hadir + stats.telat + stats.sakit + stats.izin + stats.setengah_hari} Hari
                      </div>
                    </div>
                    {member.photo_url ? (
                      <img src={member.photo_url} alt="Profile" className="w-12 h-12 rounded-full object-cover shadow-sm shrink-0 border-2 border-white" />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-[#302621] to-[#4A3B32] rounded-full flex items-center justify-center text-white font-black text-sm shrink-0 shadow-sm border-2 border-white">
                        {initials}
                      </div>
                    )}
                    <div>
                      <h4 className="font-bold text-sm text-[#302621] leading-tight">{member.full_name}</h4>
                      <p className="text-[10px] text-[#8C6208] font-bold uppercase tracking-wider mt-0.5">{member.position || member.role.replace('_', ' ')}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{member.department || 'Tanpa Departemen'}</p>
                    </div>
                  </div>
                  
                  {/* Grid of Stats */}
                  <div 
                    className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mt-auto relative group/stats cursor-pointer"
                    onClick={() => {
                      const todayObj = new Date();
                      let monthKey = `${todayObj.getFullYear()}-${String(todayObj.getMonth() + 1).padStart(2, '0')}`;
                      if (timeFilter === 'bulan_kemarin') {
                        const prev = new Date(todayObj.getFullYear(), todayObj.getMonth() - 1, 1);
                        monthKey = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
                      }
                      const mOv = member?.absensi_override_by_month?.[monthKey] || member?.absensi_override || {};

                      setStatsForm({
                        hadir: mOv.hadir !== undefined ? stats.hadir : '',
                        telat: mOv.telat !== undefined ? stats.telat : '',
                        sakit: mOv.sakit !== undefined ? stats.sakit : '',
                        izin: mOv.izin !== undefined ? stats.izin : '',
                        setengah_hari: mOv.setengah_hari !== undefined ? stats.setengah_hari : '',
                        alpa: mOv.alpa !== undefined ? stats.alpa : '',
                      });
                      setEditingStatsFor({ memberId: member.id, original: stats.original });
                    }}
                    title="Klik untuk mengubah jumlah kehadiran manual"
                  >
                    <div className="absolute -top-3 -right-2 bg-white rounded-full p-1 shadow-sm border border-slate-100 opacity-0 group-hover/stats:opacity-100 transition-opacity z-10">
                      <Pencil className="w-3.5 h-3.5 text-slate-400 hover:text-[#8C6208]" />
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-2 text-center border border-emerald-100 hover:bg-emerald-100 transition-colors overflow-hidden">
                      <p className="text-[9px] font-bold text-emerald-600 uppercase">Hadir</p>
                      <p className={`font-black text-emerald-700 ${stats.hadir > 999 ? 'text-xs sm:text-sm' : 'text-base sm:text-lg'}`}>{stats.hadir}</p>
                    </div>
                    <div className="bg-rose-50 rounded-xl p-2 text-center border border-rose-100 hover:bg-rose-100 transition-colors overflow-hidden">
                      <p className="text-[9px] font-bold text-rose-600 uppercase">Telat</p>
                      <p className={`font-black text-rose-700 ${stats.telat > 999 ? 'text-xs sm:text-sm' : 'text-base sm:text-lg'}`}>{stats.telat}</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-2 text-center border border-amber-100 hover:bg-amber-100 transition-colors overflow-hidden">
                      <p className="text-[9px] font-bold text-amber-600 uppercase tracking-tighter">Sakit</p>
                      <p className={`font-black text-amber-700 ${stats.sakit > 999 ? 'text-xs sm:text-sm' : 'text-base sm:text-lg'}`}>{stats.sakit}</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-2 text-center border border-blue-100 hover:bg-blue-100 transition-colors overflow-hidden">
                      <p className="text-[9px] font-bold text-blue-600 uppercase tracking-tighter">Izin</p>
                      <p className={`font-black text-blue-700 ${stats.izin > 999 ? 'text-xs sm:text-sm' : 'text-base sm:text-lg'}`}>{stats.izin}</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-2 text-center border border-purple-100 hover:bg-purple-100 transition-colors overflow-hidden">
                      <p className="text-[8px] font-bold text-purple-600 uppercase tracking-tighter leading-tight">1/2 Hari</p>
                      <p className={`font-black text-purple-700 ${stats.setengah_hari > 999 ? 'text-xs sm:text-sm' : 'text-base sm:text-lg'}`}>{stats.setengah_hari}</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-2 text-center border border-slate-200 hover:bg-slate-100 transition-colors overflow-hidden">
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter">Alpa</p>
                      <p className={`font-black text-slate-600 ${stats.alpa > 999 ? 'text-xs sm:text-sm' : 'text-base sm:text-lg'}`}>{stats.alpa}</p>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      const todayObj = new Date();
                      let monthKey = `${todayObj.getFullYear()}-${String(todayObj.getMonth() + 1).padStart(2, '0')}`;
                      if (timeFilter === 'bulan_kemarin') {
                        const prev = new Date(todayObj.getFullYear(), todayObj.getMonth() - 1, 1);
                        monthKey = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
                      }
                      setCalendarMonthKey(monthKey);
                      setCalendarMember(member);
                    }}
                    className="w-full mt-3 h-8 text-[11px] font-bold text-amber-900 bg-[#F9F5EC] hover:bg-[#F3ECE0] border border-[#E6D5B8] rounded-xl flex items-center justify-center gap-1.5 shadow-sm"
                  >
                    <Calendar className="w-3.5 h-3.5 text-amber-700" />
                    Kalender Presensi & Isi Tanggal Bolong
                  </Button>
                  
                  {stats.telat > 2 && (
                     <div className="mt-3 bg-red-50 text-red-700 text-[10px] font-bold p-2 rounded-lg flex items-center gap-1.5 border border-red-100">
                       <AlertTriangle className="w-3.5 h-3.5" />
                       Perhatian: Karyawan sering terlambat bulan ini.
                     </div>
                  )}
                </div>
              );
            })}
            
            {displayMembers.length === 0 && (
              <div className="col-span-full py-10 text-center text-sm text-slate-500 bg-white border border-dashed rounded-2xl">
                Belum ada data anggota tim yang wajib absensi.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dialog Lihat & Bandingkan Foto Absensi vs Acuan Wajah */}
      <Dialog open={!!viewingPhoto} onOpenChange={(open) => !open && setViewingPhoto(null)}>
        <DialogContent className="max-w-md md:max-w-2xl p-5 bg-white rounded-3xl border border-amber-200/80 shadow-2xl overflow-hidden">
          {(() => {
            const isObj = viewingPhoto && typeof viewingPhoto === 'object';
            const empPhoto = isObj ? viewingPhoto.empPhoto : viewingPhoto;
            const refPhoto = isObj ? viewingPhoto.refPhoto : null;
            const name = isObj ? viewingPhoto.name : 'Presensi Satala';
            const notes = isObj ? (viewingPhoto.notes || []) : [];
            const hasMismatch = notes.some(n => n.includes('Wajah Beda') || n.includes('Wajah Tidak Cocok'));
            const mismatchNote = notes.find(n => n.includes('AI:'));

            return (
              <div className="space-y-4">
                <DialogHeader className="flex flex-row items-center justify-between pb-2 border-b border-amber-100/70">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#8C6208] to-[#D4AF37] flex items-center justify-center shadow-xs text-white">
                      <ScanFace className="w-4 h-4" />
                    </div>
                    <div>
                      <DialogTitle className="text-sm font-black text-[#302621]">Verifikasi Foto & Acuan Wajah</DialogTitle>
                      <p className="text-[11px] text-slate-500 font-semibold">{name}</p>
                    </div>
                  </div>
                </DialogHeader>

                {/* Grid Perbandingan 2 Kolom: Acuan Profil vs Foto Absen */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Kolom 1: Foto Acuan Profil */}
                  <div className="flex flex-col bg-amber-50/40 p-3 rounded-2xl border border-amber-200/60">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                        <ScanFace className="w-3.5 h-3.5 text-[#8C6208]" /> Foto Acuan (Profil)
                      </span>
                      {refPhoto ? (
                        <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-full">
                          Terdaftar
                        </span>
                      ) : (
                        <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                          Belum Ada
                        </span>
                      )}
                    </div>

                    <div className="w-full aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 border border-amber-200/50 flex items-center justify-center relative">
                      {refPhoto ? (
                        <img src={refPhoto} alt="Acuan Wajah" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center p-4 text-slate-400 space-y-1.5">
                          <UserCheck className="w-8 h-8 opacity-40 text-slate-400" />
                          <p className="text-[11px] font-medium">Belum ada foto acuan yang didaftarkan di profil.</p>
                        </div>
                      )}
                    </div>

                    {refPhoto && (
                      <div className="mt-2 flex justify-end">
                        <a 
                          href={refPhoto} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          download={`acuan_wajah_${name}.jpg`}
                          className="text-[10px] font-bold text-[#8C6208] hover:underline"
                        >
                          Buka Foto Asli ↗
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Kolom 2: Foto Saat Presensi */}
                  <div className="flex flex-col bg-slate-50 p-3 rounded-2xl border border-slate-200/70">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                        <Camera className="w-3.5 h-3.5 text-slate-600" /> Foto Saat Presensi
                      </span>
                      {hasMismatch ? (
                        <span className="text-[9px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <AlertTriangle className="w-2.5 h-2.5" /> Beda
                        </span>
                      ) : empPhoto ? (
                        <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                          Terekam
                        </span>
                      ) : (
                        <span className="text-[9px] font-bold text-slate-400 bg-slate-200 px-2 py-0.5 rounded-full">
                          Tidak Ada
                        </span>
                      )}
                    </div>

                    <div className="w-full aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center relative">
                      {empPhoto ? (
                        <img src={empPhoto} alt="Foto Presensi" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center p-4 text-slate-400 space-y-1.5">
                          <Camera className="w-8 h-8 opacity-40 text-slate-400" />
                          <p className="text-[11px] font-medium">Tidak ada rekaman foto pada log ini.</p>
                        </div>
                      )}
                    </div>

                    {empPhoto && (
                      <div className="mt-2 flex justify-end">
                        <a 
                          href={empPhoto} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          download={`foto_presensi_${name}.jpg`}
                          className="text-[10px] font-bold text-[#8C6208] hover:underline"
                        >
                          Buka Foto Asli ↗
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Banner Status AI & Penjelasan Perbedaan */}
                {mismatchNote && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs space-y-1">
                    <p className="font-bold text-rose-800 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                      {mismatchNote}
                    </p>
                    <p className="text-[11px] text-rose-700/80 leading-relaxed">
                      AI mendeteksi perbedaan antara orang yang berhadapan di kamera saat presensi dengan foto acuan yang tersimpan di profil (Ambang batas kemiripan: jarak ≤ 0.60).
                    </p>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Dialog Edit Manual Override Stats */}
      <Dialog open={!!editingStatsFor} onOpenChange={(open) => !open && setEditingStatsFor(null)}>
        <DialogContent className="max-w-xs p-5 bg-white rounded-2xl border border-slate-100 shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-sm font-black text-[#302621]">Edit Rekap Kehadiran</DialogTitle>
          </DialogHeader>
          {editingStatsFor && (
            <div className="space-y-4 mt-2 text-xs">
              <p className="text-slate-500 mb-2">Ketik angka total akhir yang Anda inginkan (bebas hingga 10.000+). Kosongkan jika ingin kembali sesuai sistem.</p>
              {['hadir', 'telat', 'sakit', 'izin', 'setengah_hari', 'alpa'].map(key => (
                <div key={key}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-bold text-slate-500 uppercase text-[10px]">{key.replace('_', ' ')}</span>
                    <span className="text-[10px] text-emerald-600 font-bold">Asli Sistem: {editingStatsFor.original[key]}</span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max="999999"
                    value={statsForm[key] ?? ''}
                    onChange={(e) => setStatsForm(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={`Sistem: ${editingStatsFor.original[key]}`}
                    className="w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] text-xs font-semibold"
                  />
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setEditingStatsFor(null)} className="h-8 text-xs font-bold">
                  Batal
                </Button>
                <Button 
                  onClick={async () => {
                    const override = {};
                    ['hadir', 'telat', 'sakit', 'izin', 'setengah_hari', 'alpa'].forEach(key => {
                      if (statsForm[key] !== '' && statsForm[key] !== null) {
                        const targetVal = parseFloat(statsForm[key]);
                        if (!isNaN(targetVal)) {
                          override[key] = targetVal;
                        }
                      }
                    });
                    
                    const currentMonthKey = (() => {
                      const today = new Date();
                      if (timeFilter === 'bulan_kemarin') {
                        const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                        return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
                      }
                      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                    })();

                    const existingMember = teamMembers.find(m => m.id === editingStatsFor.memberId);
                    const existingByMonth = existingMember?.absensi_override_by_month || {};
                    const updatedByMonth = {
                      ...existingByMonth,
                      [currentMonthKey]: Object.keys(override).length > 0 ? override : null
                    };

                    try {
                      await base44.entities.TeamMember.update(editingStatsFor.memberId, {
                        absensi_override: Object.keys(override).length > 0 ? override : null,
                        absensi_override_by_month: updatedByMonth
                      });
                      toast.success(`Rekap bulan (${currentMonthKey}) berhasil diperbarui!`);
                      queryClient.invalidateQueries({ queryKey: ["team-members"] });
                      setEditingStatsFor(null);
                    } catch (e) {
                      toast.error("Gagal menyimpan rekap.");
                    }
                  }} 
                  className="h-8 text-xs font-bold bg-[#302621] hover:bg-[#4A3B32] text-white rounded-xl"
                >
                  Simpan Perubahan
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Kalender Kehadiran & Input Tanggal Bolong */}
      <Dialog open={!!calendarMember} onOpenChange={(open) => !open && setCalendarMember(null)}>
        <DialogContent className="max-w-3xl p-5 bg-white rounded-3xl border border-slate-100 shadow-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader className="border-b border-slate-100 pb-3">
            <DialogTitle className="text-base font-black text-[#302621] flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-700" />
                Kalender Presensi & Deteksi Tanggal Kosong
              </span>
              <div className="text-xs font-bold text-[#8C6208] bg-[#F9F5EC] px-3 py-1 rounded-full border border-[#E6D5B8]">
                {calendarMember?.full_name}
              </div>
            </DialogTitle>
          </DialogHeader>

          {calendarMember && calendarMonthKey && (
            <div className="space-y-4 mt-2">
              {/* Aesthetic Month Navigation Header */}
              <div className="flex items-center justify-between gap-2 bg-[#302621] text-white p-3 rounded-2xl shadow-sm">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    const parts = calendarMonthKey.split('-');
                    const y = parseInt(parts[0]);
                    const m = parseInt(parts[1]);
                    const prevDate = new Date(y, m - 2, 1);
                    setCalendarMonthKey(`${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`);
                  }}
                  className="h-8 text-xs font-bold text-slate-200 hover:text-white hover:bg-white/10 rounded-xl gap-1 px-2.5"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Bulan Lalu</span>
                </Button>

                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-sm font-black tracking-wide text-amber-100 uppercase">
                    {(() => {
                      const parts = calendarMonthKey.split('-');
                      const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
                      return d.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
                    })()}
                  </span>
                </div>

                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    const parts = calendarMonthKey.split('-');
                    const y = parseInt(parts[0]);
                    const m = parseInt(parts[1]);
                    const nextDate = new Date(y, m, 1);
                    setCalendarMonthKey(`${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`);
                  }}
                  className="h-8 text-xs font-bold text-slate-200 hover:text-white hover:bg-white/10 rounded-xl gap-1 px-2.5"
                >
                  <span className="hidden sm:inline">Bulan Depan</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              {/* Monthly Stats Calculation for Selected Month */}
              {(() => {
                const parts = calendarMonthKey.split('-');
                const year = parseInt(parts[0]);
                const monthIdx = parseInt(parts[1]) - 1;
                const totalDays = new Date(year, monthIdx + 1, 0).getDate();
                const todayObj = new Date();
                todayObj.setHours(23,59,59,999);

                const memberLogs = allLogs.filter(l => {
                  if (l.employee_id !== calendarMember.id || !l.timestamp) return false;
                  const d = new Date(l.timestamp);
                  return d.getFullYear() === year && d.getMonth() === monthIdx;
                });

                let countHadir = 0, countTelat = 0, countSakit = 0, countIzin = 0, countSH = 0, countAlpa = 0, countLiburNasional = 0;
                const daysArray = [];

                for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
                  const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                  const dateObj = new Date(year, monthIdx, dayNum);
                  const dayOfWeek = dateObj.getDay();
                  const isSunday = dayOfWeek === 0;
                  const isSaturday = dayOfWeek === 6;
                  const isSatOff = calendarMember?.saturday_off === true;
                  const holidayInfo = getIndonesianHoliday(dateStr);
                  const isHoliday = !!holidayInfo;
                  const isOff = isSunday || (isSaturday && isSatOff);

                  const dayLogs = memberLogs.filter(l => l.timestamp.startsWith(dateStr));
                  const joinDateStr = getMemberJoinDateStr(calendarMember);
                  const isBeforeJoin = Boolean(joinDateStr && dateStr < joinDateStr);
                  let status = 'empty';

                  if (isBeforeJoin) {
                    status = 'belum_bergabung';
                  } else if (dayLogs.length > 0) {
                    const checkIn = dayLogs.find(l => l.type === 'check_in');
                    const sakit = dayLogs.find(l => l.type === 'sakit' || (l.notes && l.notes.toLowerCase().includes('sakit')));
                    const izin = dayLogs.find(l => l.type === 'izin' || (l.notes && l.notes.toLowerCase().includes('izin')));

                    if (sakit) { status = 'sakit'; countSakit++; }
                    else if (izin) { status = 'izin'; countIzin++; }
                    else if (checkIn) {
                      if (checkIn.notes && checkIn.notes.toLowerCase().includes('setengah hari')) { status = 'setengah_hari'; countSH++; }
                      else if (checkIn.notes && checkIn.notes.toLowerCase().includes('telat')) { status = 'telat'; countTelat++; }
                      else { status = 'hadir'; countHadir++; }
                    }
                  } else if (isHoliday) {
                    status = 'libur_nasional';
                    countLiburNasional++;
                  } else if (isOff) {
                    status = 'libur';
                  } else if (dateObj > todayObj) {
                    status = 'upcoming';
                  } else {
                    status = 'alpa';
                    countAlpa++;
                  }

                  daysArray.push({ dayNum, dateStr, dateObj, status, dayLogs, holidayInfo, isSunday, isHoliday, isBeforeJoin });
                }

                return (
                  <div className="space-y-3">
                    {/* Summary Banner for Selected Month */}
                    <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 bg-[#F9F5EC] p-3 rounded-2xl border border-[#E6D5B8]/60 text-center shadow-xs">
                      <div className="bg-emerald-50 rounded-xl p-2 border border-emerald-100">
                        <p className="text-[9px] font-extrabold text-emerald-600 uppercase tracking-wider">Hadir</p>
                        <p className="font-black text-emerald-700 text-base">{countHadir} Hari</p>
                      </div>
                      <div className="bg-rose-50 rounded-xl p-2 border border-rose-100">
                        <p className="text-[9px] font-extrabold text-rose-600 uppercase tracking-wider">Telat</p>
                        <p className="font-black text-rose-700 text-base">{countTelat} Hari</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-2 border border-amber-100">
                        <p className="text-[9px] font-extrabold text-amber-600 uppercase tracking-wider">Sakit</p>
                        <p className="font-black text-amber-700 text-base">{countSakit} Hari</p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-2 border border-blue-100">
                        <p className="text-[9px] font-extrabold text-blue-600 uppercase tracking-wider">Izin</p>
                        <p className="font-black text-blue-700 text-base">{countIzin} Hari</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-2 border border-purple-100">
                        <p className="text-[9px] font-extrabold text-purple-600 uppercase tracking-wider">1/2 Hari</p>
                        <p className="font-black text-purple-700 text-base">{countSH} Hari</p>
                      </div>
                      <div className="bg-red-50/90 rounded-xl p-2 border border-red-200">
                        <p className="text-[9px] font-extrabold text-red-600 uppercase tracking-wider">Tgl Merah</p>
                        <p className="font-black text-red-700 text-base">{countLiburNasional} Hari</p>
                      </div>
                      <div className="bg-slate-100 rounded-xl p-2 border border-slate-200">
                        <p className="text-[9px] font-extrabold text-slate-500 uppercase tracking-wider">Kosong / Alpa</p>
                        <p className="font-black text-slate-700 text-base">{countAlpa} Hari</p>
                      </div>
                    </div>

                    {/* Status Legend */}
                    <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] font-bold px-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md border border-emerald-100">🟢 Hadir</span>
                        <span className="flex items-center gap-1 bg-rose-50 text-rose-700 px-2 py-0.5 rounded-md border border-rose-100">🟡 Telat</span>
                        <span className="flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-0.5 rounded-md border border-amber-100">🟠 Sakit</span>
                        <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md border border-blue-100">🔵 Izin</span>
                        <span className="flex items-center gap-1 bg-purple-50 text-purple-700 px-2 py-0.5 rounded-md border border-purple-100">🟣 1/2 Hari</span>
                        <span className="flex items-center gap-1 bg-red-100 text-red-800 px-2 py-0.5 rounded-md border border-red-200">🇮🇩 Tanggal Merah (Libur)</span>
                        <span className="flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md border border-slate-200">🔴 Kosong / Alpa</span>
                        <span className="flex items-center gap-1 bg-slate-50 text-slate-400 px-2 py-0.5 rounded-md border border-slate-200 border-dashed">⚪ Belum Bergabung</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-semibold italic">Klik tanggal untuk ubah status</span>
                    </div>

                    {/* Aesthetic Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1.5 pt-1">
                      {['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'].map((dName, i) => (
                        <div key={dName} className={`text-center text-[10px] font-black uppercase py-1 ${i === 0 ? 'text-rose-500' : 'text-slate-500'}`}>
                          {dName}
                        </div>
                      ))}

                      {/* Day padding */}
                      {Array.from({ length: new Date(year, monthIdx, 1).getDay() }).map((_, i) => (
                        <div key={`pad-${i}`} className="h-16 bg-slate-50/40 rounded-2xl border border-slate-100 opacity-20" />
                      ))}

                      {daysArray.map(d => {
                        const isBeforeJoin = d.status === 'belum_bergabung';
                        const isAlpa = d.status === 'alpa';
                        const isHadir = d.status === 'hadir';
                        const isTelat = d.status === 'telat';
                        const isSakit = d.status === 'sakit';
                        const isIzin = d.status === 'izin';
                        const isSH = d.status === 'setengah_hari';
                        const isLibur = d.status === 'libur';
                        const isLiburNasional = d.status === 'libur_nasional';
                        const hasPhoto = d.dayLogs?.some(l => l.photo_url);
                        const isRedDay = d.isHoliday || d.isSunday;

                        return (
                          <div
                            key={d.dateStr}
                            title={isBeforeJoin ? 'Belum bergabung ke perusahaan' : d.holidayInfo ? `${d.holidayInfo.name} (Libur Nasional)` : undefined}
                            onClick={() => {
                              if (d.status !== 'upcoming' && !isBeforeJoin) {
                                setDateStatusModal({ memberId: calendarMember.id, dateStr: d.dateStr, currentStatus: d.status, dayLogs: d.dayLogs, holidayInfo: d.holidayInfo });
                              }
                            }}
                            className={`h-16 rounded-2xl p-2 flex flex-col justify-between border transition-all shadow-xs relative group ${
                              isBeforeJoin ? 'bg-slate-50/70 border-dashed border-slate-200 text-slate-400 opacity-60 cursor-default' :
                              'cursor-pointer hover:scale-[1.03]'
                            } ${
                              isBeforeJoin ? '' :
                              isLiburNasional ? 'bg-red-50/90 border-red-200 text-red-800 hover:border-red-300' :
                              isAlpa ? 'bg-slate-100 border-slate-200 hover:border-slate-300 text-slate-700' :
                              isHadir ? 'bg-emerald-50 border-emerald-100 text-emerald-700' :
                              isTelat ? 'bg-rose-50 border-rose-100 text-rose-700' :
                              isSakit ? 'bg-amber-50 border-amber-100 text-amber-700' :
                              isIzin ? 'bg-blue-50 border-blue-100 text-blue-700' :
                              isSH ? 'bg-purple-50 border-purple-100 text-purple-700' :
                              isLibur ? 'bg-slate-50/60 border-slate-100 text-slate-400' :
                              'bg-white border-slate-200 text-slate-400'
                            }`}
                          >
                            <div className="flex justify-between items-center text-[11px] font-black">
                              <span className={`flex items-center gap-1 ${isRedDay && !isBeforeJoin ? 'text-red-600 font-black' : isBeforeJoin ? 'text-slate-400' : ''}`}>
                                {d.dayNum}
                                {hasPhoto && <span title="Ada Foto Scan Wajah Asli" className="text-[10px]">📷</span>}
                              </span>
                              {!isBeforeJoin && <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 text-slate-400 transition-opacity" />}
                            </div>

                            <div className="text-[9px] font-extrabold truncate">
                              {isBeforeJoin && <span className="text-slate-400 font-medium">Belum Gabung</span>}
                              {isLiburNasional && (
                                <div className="truncate">
                                  <span className="text-red-700 font-black text-[8px] bg-red-100/90 px-1 py-0.5 rounded flex items-center gap-0.5 truncate">
                                    🇮🇩 {d.holidayInfo?.name ? d.holidayInfo.name.split('(')[0].trim() : 'Libur'}
                                  </span>
                                </div>
                              )}
                              {isAlpa && <span className="bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded-md text-[8px] tracking-tight">🔴 Kosong</span>}
                              {isHadir && <span className="text-emerald-700 font-extrabold">{isRedDay ? '🟢 Lembur' : '🟢 Hadir'}</span>}
                              {isTelat && <span className="text-rose-700 font-extrabold">🟡 Telat</span>}
                              {isSakit && <span className="text-amber-700 font-extrabold">🟠 Sakit</span>}
                              {isIzin && <span className="text-blue-700 font-extrabold">🔵 Izin</span>}
                              {isSH && <span className="text-purple-700 font-extrabold">🟣 1/2 Hari</span>}
                              {isLibur && <span className="text-slate-400 font-medium">Libur</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Edit Jam Presensi (Masuk / Pulang) */}
      <Dialog open={!!editingLog} onOpenChange={(open) => !open && setEditingLog(null)}>
        <DialogContent className="max-w-md p-6 bg-white rounded-3xl border border-amber-200/70 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-black text-[#302621] flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#8C6208]" />
              Edit Jam {editingLog?.type === 'check_in' ? 'Masuk (Check In)' : 'Pulang (Check Out)'}
            </DialogTitle>
          </DialogHeader>

          {editingLog && (
            <div className="space-y-4 mt-2 text-xs">
              <div className="p-3 bg-amber-50/60 border border-amber-200/80 rounded-2xl flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-amber-800 font-bold uppercase tracking-wider block">Karyawan</span>
                  <span className="text-sm font-black text-[#302621]">{editingLog.employeeName}</span>
                </div>
                <Badge variant="outline" className="border-amber-300 text-[#8C6208] bg-white font-bold text-[10px]">
                  {editingLog.type === 'check_in' ? '🟢 Check In' : '🔴 Check Out'}
                </Badge>
              </div>

              <div>
                <label className="font-bold text-[#7A695B] mb-1.5 block">
                  Tanggal & Jam {editingLog.type === 'check_in' ? 'Masuk' : 'Pulang'}
                </label>
                <Input
                  type="datetime-local"
                  value={editForm.timestamp}
                  onChange={(e) => setEditForm(prev => ({ ...prev, timestamp: e.target.value }))}
                  className="h-10 text-xs font-mono font-bold rounded-xl border-[#E6D5B8] bg-slate-50/50"
                />
                <span className="text-[10px] text-slate-400 mt-1 block">
                  Format waktu WIB. Jam masuk standar kantor adalah pukul 08:00 WIB.
                </span>
              </div>

              <div>
                <label className="font-bold text-[#7A695B] mb-1.5 block">Catatan / Alasan Koreksi</label>
                <Input
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Cth: Koreksi jam masuk disetujui HRD / kendala fingerprint..."
                  className="h-9 text-xs rounded-xl border-[#E6D5B8]"
                />
              </div>

              <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    handleDeleteLog(editingLog.id);
                    setEditingLog(null);
                  }}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 text-xs flex items-center gap-1 cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Hapus Log Ini
                </Button>

                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditingLog(null)} className="text-xs cursor-pointer">
                    Batal
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleSaveEdit}
                    className="bg-[#8C6208] hover:bg-[#6e4c06] text-white text-xs font-bold px-4 rounded-xl shadow-xs cursor-pointer"
                  >
                    Simpan Perubahan
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Ubah Status Presensi Tanggal Spesifik */}
      <Dialog open={!!dateStatusModal} onOpenChange={(open) => !open && setDateStatusModal(null)}>
        <DialogContent className="max-w-xs p-5 bg-white rounded-2xl border border-slate-100 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-sm font-black text-[#302621]">
              Isi Presensi Tanggal {dateStatusModal?.dateStr}
            </DialogTitle>
          </DialogHeader>
          {dateStatusModal && (
            <div className="space-y-2 mt-2">
              {(() => {
                const photoLog = dateStatusModal?.dayLogs?.find(l => l.photo_url);
                if (photoLog) {
                  return (
                    <div className="mb-3 p-2.5 bg-[#F9F5EC] border border-[#E6D5B8] rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src={photoLog.photo_url} alt="Face" className="w-8 h-8 rounded-lg object-cover border border-amber-200" />
                        <span className="text-[10px] font-bold text-[#6D4C06]">Foto Terverifikasi 📷</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-[9px] h-6 px-2 font-bold border-amber-300 text-[#8C6208] bg-white hover:bg-amber-50"
                        onClick={() => setViewingPhoto(photoLog.photo_url)}
                      >
                        Lihat Foto
                      </Button>
                    </div>
                  );
                }
                return null;
              })()}
              <p className="text-xs text-slate-500 mb-3">Pilih status kehadiran yang Anda inginkan untuk tanggal ini:</p>
              
              <Button 
                onClick={() => applyDateStatus('hadir')} 
                className="w-full justify-start h-9 text-xs font-bold bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200"
              >
                🟢 Hadir (Masuk Penuh)
              </Button>

              <Button 
                onClick={() => applyDateStatus('telat')} 
                className="w-full justify-start h-9 text-xs font-bold bg-rose-50 text-rose-800 hover:bg-rose-100 border border-rose-200"
              >
                🟡 Telat (Masuk Terlambat)
              </Button>

              <Button 
                onClick={() => applyDateStatus('sakit')} 
                className="w-full justify-start h-9 text-xs font-bold bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200"
              >
                🟠 Sakit
              </Button>

              <Button 
                onClick={() => applyDateStatus('izin')} 
                className="w-full justify-start h-9 text-xs font-bold bg-blue-50 text-blue-800 hover:bg-blue-100 border border-blue-200"
              >
                🔵 Izin
              </Button>

              <Button 
                onClick={() => applyDateStatus('setengah_hari')} 
                className="w-full justify-start h-9 text-xs font-bold bg-purple-50 text-purple-800 hover:bg-purple-100 border border-purple-200"
              >
                🟣 1/2 Hari (Setengah Hari)
              </Button>

              <Button 
                onClick={() => applyDateStatus('alpa')} 
                variant="outline"
                className="w-full justify-start h-9 text-xs font-bold text-red-600 border-red-200 hover:bg-red-50"
              >
                🔴 Kosongkan / Set Alpa (Bolong)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
